import { Controller } from "@hotwired/stimulus"

// Cross-column selection for workflow boards. Selection stays intentionally
// local to the loaded board: it is visible, reviewable, and handed to the same
// bulk endpoints used by the workflow table.
export default class extends Controller {
  static targets = [
    "checkbox",
    "toolbar",
    "count",
    "error",
    "stateButton",
    "reassignButton",
    "stateAction",
    "reassignAction",
  ]

  static values = {
    assignListUrl: String,
    tasksUrl: String,
  }

  initialize() {
    this.selectedItems = new Map()
    this.lastCheckbox = null
  }

  connect() {
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this.boundHandleListSelection = this.handleListSelection.bind(this)
    this.boundRenderSelection = this.renderSelection.bind(this)
    document.addEventListener("board:refreshed", this.boundRenderSelection)
    this.boundHandleBulkCompletion = this.handleBulkCompletion.bind(this)
    document.addEventListener("click", this.boundHandleListSelection)
    document.addEventListener("processes-bulk-actioned", this.boundHandleBulkCompletion)
    this.renderSelection()
  }

  disconnect() {
    document.removeEventListener("board:refreshed", this.boundRenderSelection)
    document.removeEventListener("click", this.boundHandleListSelection)
    document.removeEventListener("processes-bulk-actioned", this.boundHandleBulkCompletion)
  }

  checkboxTargetConnected(checkbox) {
    checkbox.checked = this.selectedItems.has(checkbox.dataset.itemId)
    if (checkbox.checked) this.storeCheckbox(checkbox)
    this.renderCardSelection(checkbox)
    this.renderSelection()
  }

  checkboxTargetDisconnected() {
    this.renderSelection()
  }

  toggle(event) {
    event.stopPropagation()
    const checkbox = event.currentTarget

    if (event.shiftKey && this.lastCheckbox && this.lastCheckbox !== checkbox) {
      const checkboxes = this.checkboxTargets
      const anchorIndex = checkboxes.indexOf(this.lastCheckbox)
      const targetIndex = checkboxes.indexOf(checkbox)

      if (anchorIndex >= 0 && targetIndex >= 0) {
        const [from, to] = anchorIndex < targetIndex ? [anchorIndex, targetIndex] : [targetIndex, anchorIndex]
        checkboxes.slice(from, to + 1).forEach(candidate => {
          candidate.checked = checkbox.checked
          this.storeCheckbox(candidate)
        })
      }
    } else {
      this.storeCheckbox(checkbox)
    }

    this.lastCheckbox = checkbox
    this.renderSelection()
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  // The List view's header box: select every item on the open page of one
  // list, or clear them when they are all selected already.
  selectColumn(event) {
    event.stopPropagation()
    const key = event.currentTarget.dataset.columnKey
    const boxes = this.checkboxTargets.filter(checkbox => checkbox.closest("[data-board-column-key]")?.dataset.boardColumnKey === key)
    const select = !boxes.every(checkbox => checkbox.checked)
    boxes.forEach(checkbox => {
      checkbox.checked = select
      this.storeCheckbox(checkbox)
    })
    event.currentTarget.checked = select && boxes.length > 0
    this.renderSelection()
  }

  clear(event) {
    event?.preventDefault()
    this.selectedItems.clear()
    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = false
      this.renderCardSelection(checkbox)
    })
    this.lastCheckbox = null
    this.renderSelection()
  }

  openAssignList(event) {
    event.preventDefault()
    if (this.selectedItems.size === 0) return

    document.dispatchEvent(new CustomEvent("quick-selector:open", {
      detail: { id: "board_bulk_assign_list" },
    }))
  }

  openState(event) {
    event.preventDefault()
    if (!this.allSelectedManageable) return
    this.openBulkModal(this.stateActionTarget)
  }

  openReassign(event) {
    event.preventDefault()
    if (!this.allSelectedManageable) return
    this.openBulkModal(this.reassignActionTarget)
  }

  viewTasks(event) {
    event.preventDefault()
    if (this.selectedItems.size === 0) return

    const url = new URL(this.tasksUrlValue, window.location.origin)
    url.searchParams.set("process_ids", this.selectedIds.join(','))
    url.searchParams.set("inline_actions", "true")
    window.location.assign(url.toString())
  }

  handleListSelection(event) {
    const item = event.target.closest("[data-board-bulk-list-id]")
    if (!item || this.selectedItems.size === 0) return

    event.preventDefault()
    this.assignList(item.dataset.boardBulkListId)
  }

  async assignList(listId) {
    if (this.busy) return
    this.setBusy(true)
    this.showError("")

    try {
      const response = await fetch(this.assignListUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "text/vnd.turbo-stream.html, text/html, application/xhtml+xml",
        },
        body: JSON.stringify({
          references: this.selectedReferences.join(','),
          list_id: listId || null,
        }),
      })

      if (!response.ok) throw new Error("The selected workflows could not be moved.")

      const html = await response.text()
      if (html.trim().startsWith("<turbo-stream")) Turbo.renderStreamMessage(html)
      this.clear()
      this.setBusy(false)
      document.dispatchEvent(new CustomEvent("board:refresh"))
    } catch (error) {
      this.showError(error.message || "The selected workflows could not be moved.")
      this.setBusy(false)
    }
  }

  handleBulkCompletion() {
    this.clear()
    document.dispatchEvent(new CustomEvent("board:refresh"))
  }

  openBulkModal(target) {
    const ids = this.selectedIds
    if (ids.length === 0) return

    const url = new URL(target.dataset.baseUrl, window.location.origin)
    ids.forEach(id => url.searchParams.append('ids[]', id))
    target.href = url.toString()
    target.click()
  }

  storeCheckbox(checkbox) {
    if (checkbox.checked) {
      this.selectedItems.set(checkbox.dataset.itemId, {
        reference: checkbox.value,
        canManage: checkbox.dataset.canManage === "true",
      })
    } else {
      this.selectedItems.delete(checkbox.dataset.itemId)
    }
    this.renderCardSelection(checkbox)
  }

  renderSelection() {
    const count = this.selectedItems.size
    const visible = this.checkboxTargets.filter(checkbox => this.selectedItems.has(checkbox.dataset.itemId)).length
    if (this.hasCountTarget) this.countTarget.textContent = count > visible ? `${count} (${count - visible} off page)` : count
    if (this.hasToolbarTarget) this.toolbarTarget.classList.toggle("hidden", count === 0)

    // The List view's page boxes follow their rows, so they never stay ticked
    // after a row is cleared or a new page loads.
    this.element.querySelectorAll('input[data-action~="click->board-selection#selectColumn"]').forEach(box => {
      const rows = this.checkboxTargets.filter(checkbox => checkbox.closest("[data-board-column-key]")?.dataset.boardColumnKey === box.dataset.columnKey)
      box.checked = rows.length > 0 && rows.every(checkbox => checkbox.checked)
    })

    const disabled = this.busy || count === 0 || !this.allSelectedManageable
    const managedActionButtons = [...this.stateButtonTargets, ...this.reassignButtonTargets]
    managedActionButtons.forEach(button => {
      button.disabled = disabled
      button.title = disabled && count > 0 ? "Some selected workflows cannot be managed by your role" : ""
    })
  }

  // Cards take a ring; rows share hairlines and have no room for one, so the
  // row layout tints the row from the --selected class instead.
  renderCardSelection(checkbox) {
    const card = checkbox.closest(".kanban-card")
    if (!card) return
    card.classList.toggle("kanban-card--selected", checkbox.checked)
  }

  setBusy(busy) {
    this.busy = busy
    this.checkboxTargets.forEach(checkbox => { checkbox.disabled = busy })
    this.toolbarTarget.querySelectorAll("button").forEach(button => { button.disabled = busy })
    this.renderSelection()
    if (this.hasToolbarTarget) this.toolbarTarget.setAttribute("aria-busy", busy.toString())
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.classList.toggle("hidden", !message)
  }

  get selectedIds() {
    return Array.from(this.selectedItems.keys())
  }

  get selectedReferences() {
    return Array.from(this.selectedItems.values(), item => item.reference)
  }

  get allSelectedManageable() {
    return this.selectedItems.size > 0 && Array.from(this.selectedItems.values()).every(item => item.canManage)
  }
}
